function pad2(n) {
  return String(n).padStart(2, '0');
}

export function createTaskId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function startOfMinute(date) {
  const d = new Date(date);
  d.setSeconds(0, 0);
  return d;
}

export function addMinutes(date, minutes) {
  const d = new Date(date);
  d.setMinutes(d.getMinutes() + minutes);
  return d;
}

export function addHours(date, hours) {
  return addMinutes(date, hours * 60);
}

export function subtractMinutes(date, minutes) {
  return addMinutes(date, -minutes);
}

export function formatHHMM(date) {
  const d = new Date(date);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function combineDateAndTime(baseDate, hhmm) {
  if (!hhmm) return null;
  const [h, m] = String(hhmm).split(':').map((v) => parseInt(v, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const d = new Date(baseDate);
  d.setHours(h, m, 0, 0);
  return d;
}

export function detectPriority(taskText) {
  const text = String(taskText || '').toLowerCase();
  const highKeywords = [
    'notify physician',
    'call doctor',
    'urgent',
    'immediately',
    'stat',
    'if deteriorates',
    'if drops',
    'ابلغ الدكتور',
    'فورا',
    'عاجل',
  ];
  return highKeywords.some((k) => text.includes(k)) ? 'high' : 'normal';
}

export function extractDueTime(taskText, sourceTime) {
  const text = String(taskText || '').toLowerCase();
  const source = new Date(sourceTime);

  if (text.includes('in 1 hour') || text.includes('بعد ساعة')) {
    return addHours(source, 1).toISOString();
  }
  if (text.includes('in 2 hours') || text.includes('بعد ساعتين')) {
    return addHours(source, 2).toISOString();
  }
  if (text.includes('in 30 minutes') || text.includes('بعد نص ساعة')) {
    return addMinutes(source, 30).toISOString();
  }
  if (text.includes('every hour') || text.includes('كل ساعة')) {
    return addHours(source, 1).toISOString();
  }
  if (text.includes('morning') || text.includes('الصبح')) {
    const tomorrow = new Date(source);
    tomorrow.setHours(8, 0, 0, 0);
    if (tomorrow <= source) tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString();
  }

  const timeMatch = text.match(/(\d{1,2}):(\d{2})\s*(am|pm)?/);
  if (timeMatch) {
    const due = new Date(source);
    let hours = parseInt(timeMatch[1], 10);
    const minutes = parseInt(timeMatch[2], 10);
    const ampm = timeMatch[3];
    if (ampm === 'pm' && hours !== 12) hours += 12;
    if (ampm === 'am' && hours === 12) hours = 0;
    due.setHours(hours, minutes, 0, 0);
    if (due <= source) due.setDate(due.getDate() + 1);
    return due.toISOString();
  }

  return null;
}

export function humanizeDue({ dueTime, windowEnd }, now = new Date()) {
  if (!dueTime) return { text: 'Ongoing', tone: 'ongoing' };
  const due = new Date(dueTime);
  const diffMs = due.getTime() - now.getTime();
  const diffMin = Math.round(diffMs / 60000);

  if (windowEnd && new Date(windowEnd) < now) {
    const overdueMin = Math.max(1, Math.round((now.getTime() - new Date(windowEnd).getTime()) / 60000));
    return { text: `Overdue by ${overdueMin} minutes`, tone: 'overdue' };
  }
  if (diffMin <= 30) return { text: `Due in ${Math.max(0, diffMin)} minutes`, tone: 'soon' };
  return { text: `Due at ${formatHHMM(due)}`, tone: 'upcoming' };
}

export const OUTSTANDING_TASK_OWNER_PRIORITY = [
  'progress_note',
  'medication_admin',
  'handover',
  'vitals',
  'fluid_balance',
];

export function normalizeTaskTitleForDedup(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/\b(the|a|an)\b/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function areTaskTitlesEquivalent(a, b) {
  const left = normalizeTaskTitleForDedup(a);
  const right = normalizeTaskTitleForDedup(b);
  if (!left || !right) return false;
  if (left === right) return true;

  const shorter = left.length <= right.length ? left : right;
  const longer = left.length > right.length ? left : right;
  if (shorter.length >= 10 && longer.includes(shorter)) return true;

  return false;
}

const SPURIOUS_OUTSTANDING_TASK_TITLES = new Set([
  'progress note',
  'vitals',
  'vital signs',
  'blood pressure',
  'bp',
  'medications',
  'medication',
  'mar',
  'shift handover',
  'handover',
  'fluid balance',
  'sbar',
  'soap',
]);

export function isSpuriousOutstandingTask(text) {
  const normalized = normalizeTaskTitleForDedup(text);
  if (!normalized) return true;
  if (SPURIOUS_OUTSTANDING_TASK_TITLES.has(normalized)) return true;
  if (/^(complete|do|record|update|check)\s+(the\s+)?(progress note|vitals|blood pressure|medications?|handover|mar)\b/.test(normalized)) {
    return true;
  }
  return false;
}

export function filterValidOutstandingTasks(tasks = []) {
  return dedupeOutstandingTaskTexts(tasks).filter((task) => !isSpuriousOutstandingTask(task));
}

export function dedupeOutstandingTaskTexts(tasks = []) {
  const result = [];
  tasks.forEach((task) => {
    const text = String(task || '').trim();
    if (!text) return;
    if (result.some((existing) => areTaskTitlesEquivalent(existing, text))) return;
    result.push(text);
  });
  return result;
}

export function getOutstandingTaskOwnerType(generatedNotes = []) {
  const entries = generatedNotes.filter((entry) => entry?.note);
  const withTasks = entries.filter((entry) =>
    (entry.note.outstanding_tasks || []).some((task) => String(task || '').trim())
  );

  if (withTasks.length) {
    return (
      OUTSTANDING_TASK_OWNER_PRIORITY.find((type) => withTasks.some((entry) => entry.type === type)) ||
      withTasks[0].type
    );
  }

  return (
    OUTSTANDING_TASK_OWNER_PRIORITY.find((type) => entries.some((entry) => entry.type === type)) ||
    entries[0]?.type ||
    null
  );
}

export function consolidateOutstandingTasksForEncounter(generatedNotes = []) {
  const ownerType = getOutstandingTaskOwnerType(generatedNotes);
  const allTasks = [];

  generatedNotes.forEach((entry) => {
    (entry.note?.outstanding_tasks || []).forEach((task) => allTasks.push(task));
  });

  const deduped = filterValidOutstandingTasks(allTasks);

  return generatedNotes.map((entry) => {
    if (!entry.note) return entry;
    if (entry.type === ownerType) {
      return {
        ...entry,
        note: {
          ...entry.note,
          outstanding_tasks: deduped,
        },
      };
    }
    return {
      ...entry,
      note: {
        ...entry.note,
        outstanding_tasks: [],
      },
    };
  });
}

export function patientHasEquivalentGeneratedTask(existingTasks = [], taskText) {
  return existingTasks.some(
    (task) =>
      task.type === 'generated' &&
      ['pending', 'overdue', 'ongoing', 'in_progress'].includes(task.status) &&
      areTaskTitlesEquivalent(task.title, taskText)
  );
}

